/**
 * SpectrumIntake (mobile RN) — the per-tool scenario diagnostic. Folds answers
 * into a start position + locked subtype. Mirrors the web component.
 */
import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { DISPLAY_FONT, makeThemedStyles } from '../v10/theme';
import { GlassCard, Kicker } from '../v10/ui';
import { saveIntake } from './spectrum-data';
import { Pressable } from 'react-native';
import { foldIntake, DmtTool, IntakeOption } from './spectrum-tools';

export function SpectrumIntake({
  tool,
  userId,
  onDone,
}: {
  tool: DmtTool;
  userId: string | undefined;
  onDone: () => void;
}) {
  const styles = useStyles();
  const [i, setI] = useState(0);
  const [chosen, setChosen] = useState<IntakeOption[]>([]);
  const [saving, setSaving] = useState(false);

  const question = tool.intake[i];
  const last = i === tool.intake.length - 1;

  const pick = async (opt: IntakeOption) => {
    const next = [...chosen, opt];
    setChosen(next);
    if (!last) {
      setI(i + 1);
      return;
    }
    if (!userId) return;
    setSaving(true);
    const { startPosition, subtype } = foldIntake(tool, next);
    await saveIntake(supabase, userId, tool.id, { picks: next.map((o) => o.label) }, subtype, startPosition);
    onDone();
  };

  return (
    <View style={styles.container}>
      <Kicker style={styles.kicker}>
        {tool.name} · question {i + 1} of {tool.intake.length}
      </Kicker>
      <Text style={styles.prompt}>{question.prompt}</Text>

      <View style={styles.options}>
        {question.options.map((opt) => (
          <Pressable key={opt.label} disabled={saving} onPress={() => pick(opt)}>
            <GlassCard style={styles.option}>
              <Text style={styles.optionText}>{opt.label}</Text>
            </GlassCard>
          </Pressable>
        ))}
      </View>

      <Text style={styles.footer}>
        No grade, no shame — this just marks where you are so progress from here is the game.
      </Text>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    container: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24 },
    kicker: { textAlign: 'center', letterSpacing: 3 },
    prompt: { fontFamily: DISPLAY_FONT, fontSize: 20, color: t.ivory, textAlign: 'center' },
    options: { width: '100%', maxWidth: 360, gap: 10 },
    option: {
      borderRadius: 14,
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    optionText: { fontSize: 14, color: t.ivory },
    footer: { maxWidth: 360, fontSize: 11, color: t.ivoryFaint, textAlign: 'center' },
  }),
);
